/**
 * Expand a recurring personal event into concrete occurrences within a visible
 * window. Recurrence is resolved CLIENT-SIDE in the viewer's local timezone so
 * each occurrence lands at the same wall-clock time on every selected weekday
 * (DST-correct), matching how the rest of the calendar renders.
 *
 * The base item supplies the time-of-day + duration and the series start (its
 * own start date). Occurrences are produced only on/after the series start, on
 * the selected weekdays, up to recurrence_until (inclusive) when set, and always
 * clamped to the [rangeFrom, rangeTo] window so the loop stays bounded.
 */
import { CalendarItem } from '../../types';

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Human summary of a weekly rule, e.g. "Weekly on Mon, Wed, Fri". */
export function recurrenceSummary(days: number[] | null | undefined): string {
  if (!days?.length) return 'Weekly';
  const ordered = [...new Set(days)].sort((a, b) => a - b);
  if (ordered.length === 7) return 'Every day';
  if (ordered.length === 5 && ordered.every((d) => d >= 1 && d <= 5)) return 'Every weekday';
  return `Weekly on ${ordered.map((d) => WEEKDAY_LABELS[d]).join(', ')}`;
}

export function expandOccurrences(item: CalendarItem, rangeFrom: Date, rangeTo: Date): CalendarItem[] {
  if (item.recurrence !== 'weekly' || !item.recurrence_days?.length) return [item];

  const base = new Date(item.start);
  const baseEnd = new Date(item.end);
  if (Number.isNaN(base.getTime()) || Number.isNaN(baseEnd.getTime())) return [item];
  const durationMs = Math.max(0, baseEnd.getTime() - base.getTime());
  const days = new Set(item.recurrence_days);

  let until: Date | null = null;
  if (item.recurrence_until) {
    const [y, m, dd] = item.recurrence_until.split('-').map(Number);
    if (y && m && dd) until = startOfDay(new Date(y, m - 1, dd)); // inclusive local date
  }

  const seriesStart = startOfDay(base);
  const winStart = startOfDay(rangeFrom);
  const winEnd = startOfDay(rangeTo);
  let d = seriesStart > winStart ? seriesStart : winStart;

  const out: CalendarItem[] = [];
  for (; d <= winEnd; d = addDays(d, 1)) {
    if (until && d > until) break;
    if (!days.has(d.getDay())) continue;
    const occStart = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      base.getHours(),
      base.getMinutes(),
      base.getSeconds(),
      0
    );
    const occEnd = new Date(occStart.getTime() + durationMs);
    out.push({ ...item, start: occStart.toISOString(), end: occEnd.toISOString(), instance_key: dayKey(d) });
  }
  return out;
}
